globalThis.MusklePhase3 = (function phase3State() {
  const KEY = "muskle-alex-demo-phase3";
  const LEGACY_ACCOUNT_PHOTO = "/demo/photos/alex-portrait.jpg";

  function defaultAccountPhoto() {
    const marked = globalThis.document?.body?.getAttribute("data-alex-photo");
    return marked || "/demo/alex-demo.jpg";
  }

  function normalizeAccountPhoto(src) {
    if (!src || src === LEGACY_ACCOUNT_PHOTO) return defaultAccountPhoto();
    return src;
  }

  function clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function freshState() {
    return {
      accountPhoto: defaultAccountPhoto(),
      directoryConsent: false,
      publishConsent: false,
      demoPhase: 2,
      reviewStatus: "draft",
      adminNote: "",
      heroId: "cover",
      coverId: "cover",
      photos: [
        { id: "cover", src: "/demo/photos/gym-wide.jpg", caption: "" },
        { id: "form", src: "/demo/photos/trainer-form.jpg", caption: "" },
        { id: "coach", src: "/demo/photos/trainer-coach.jpg", caption: "" },
        { id: "group", src: "/demo/photos/trainer-group.jpg", caption: "" },
      ],
      draft: emptyDraft(),
      approved: null,
    };
  }

  function emptyDraft() {
    return {
      publicName: "",
      company: "",
      category: "trainer",
      intro: "",
      description: "",
      city: "",
      region: "",
      online: true,
      physical: true,
      activities: "",
      specialisms: "",
      website: "",
      instagram: "",
      tiktok: "",
      facebook: "",
      youtube: "",
      linkedin: "",
    };
  }

  function contentSignature(draft, photos, heroId, coverId) {
    return JSON.stringify({
      draft,
      photos: (photos || []).map((photo) => ({ id: photo.id, src: photo.src, caption: photo.caption || "" })),
      heroId: heroId || "",
      coverId: coverId || "",
    });
  }

  function approvedSignature(state) {
    if (!state.approved) return "";
    return contentSignature(state.approved.draft, state.approved.photos, state.approved.heroId, state.approved.coverId);
  }

  function currentSignature(state) {
    return contentSignature(state.draft, state.photos, state.heroId, state.coverId);
  }

  function isDirty(state) {
    return Boolean(state.approved) && currentSignature(state) !== approvedSignature(state);
  }

  function withAccountPhoto(state, src) {
    return { ...state, accountPhoto: src || "" };
  }

  function withDirectoryConsent(state, yes) {
    return { ...state, directoryConsent: Boolean(yes) };
  }

  function withPublishConsent(state, yes) {
    return { ...state, publishConsent: Boolean(yes) };
  }

  function withDemoPhase(state, phase) {
    const current = Number(state.demoPhase) || 2;
    const next = Math.max(current, Number(phase) || 2);
    return next === current ? state : { ...state, demoPhase: next };
  }

  function saveDraft(state, patch) {
    const next = {
      ...state,
      draft: { ...emptyDraft(), ...state.draft, ...(patch.draft || {}) },
      photos: Array.isArray(patch.photos) ? patch.photos.slice(0, 8) : state.photos,
      heroId: patch.heroId ?? state.heroId,
      coverId: patch.coverId ?? state.coverId,
    };
    if (!next.photos.some((photo) => photo.id === next.heroId)) next.heroId = next.photos[0]?.id || "";
    if (!next.photos.some((photo) => photo.id === next.coverId)) next.coverId = next.heroId;
    if (!state.approved) return next;
    const dirty = contentSignature(next.draft, next.photos, next.heroId, next.coverId) !== approvedSignature(state);
    if (!dirty && (next.reviewStatus === "draft" || next.reviewStatus === "approved")) next.reviewStatus = "approved";
    else if (dirty && (next.reviewStatus === "approved" || next.reviewStatus === "submitted")) next.reviewStatus = "draft";
    return next;
  }

  function submit(state) {
    const name = String(state.draft.publicName || "").trim();
    const intro = String(state.draft.intro || "").trim();
    if (!name || !intro) return { ok: false, error: "required" };
    return { ok: true, state: { ...state, reviewStatus: "submitted" } };
  }

  function approve(state) {
    if (state.reviewStatus !== "submitted") return { ok: false, error: "submit" };
    return {
      ok: true,
      state: {
        ...state,
        reviewStatus: "approved",
        adminNote: "",
        approved: {
          draft: clone(state.draft),
          photos: clone(state.photos),
          heroId: state.heroId,
          coverId: state.coverId,
        },
      },
    };
  }

  function requestChanges(state, note) {
    if (state.reviewStatus !== "submitted") return { ok: false, error: "submit" };
    return { ok: true, state: { ...state, reviewStatus: "changes_requested", adminNote: String(note || "").trim() } };
  }

  function rejectProfile(state, note) {
    if (state.reviewStatus !== "submitted") return { ok: false, error: "submit" };
    return { ok: true, state: { ...state, reviewStatus: "rejected", adminNote: String(note || "").trim() } };
  }

  function ambassadorCard(state) {
    if (!state.directoryConsent || !state.accountPhoto) return null;
    return { name: "Alex Demo", photo: state.accountPhoto };
  }

  function publicProfile(state) {
    if (!state.publishConsent || !state.approved) return null;
    return state.approved;
  }

  function sharePreview(state) {
    return { name: "Alex Demo", photo: state.accountPhoto || "", code: "VOORBEELD10" };
  }

  function loadState() {
    try {
      const raw = globalThis.localStorage?.getItem(KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || parsed.version !== 1 || !parsed.state?.draft) return null;
      const state = { ...freshState(), ...parsed.state, draft: { ...emptyDraft(), ...parsed.state.draft } };
      state.accountPhoto = normalizeAccountPhoto(state.accountPhoto);
      return state;
    } catch {
      return null;
    }
  }

  function storeState(state) {
    const payload = JSON.stringify({ version: 1, state });
    globalThis.localStorage.setItem(KEY, payload);
    return state;
  }

  function clearState() {
    globalThis.localStorage?.removeItem(KEY);
  }

  return {
    KEY,
    freshState,
    emptyDraft,
    isDirty,
    withAccountPhoto,
    withDirectoryConsent,
    withPublishConsent,
    withDemoPhase,
    saveDraft,
    submit,
    approve,
    requestChanges,
    rejectProfile,
    ambassadorCard,
    publicProfile,
    sharePreview,
    loadState,
    storeState,
    clearState,
    clone,
  };
})();
